import { Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export default function AddToCartDialog({
  selectedItem,
  onClose,
  modifiers,
  selectedModifiers,
  onToggleModifier,
  specialInstructions,
  setSpecialInstructions,
  quantity,
  setQuantity,
  calculateItemTotal,
  onAddToCart
}) {
  return (
    <Dialog open={!!selectedItem} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{selectedItem?.name}</DialogTitle>
          <DialogDescription className="sr-only">Add {selectedItem?.name} to your cart</DialogDescription>
        </DialogHeader>
        
        {selectedItem?.image_url && (
          <img
            src={selectedItem.image_url}
            alt={selectedItem.name}
            className="w-full h-48 object-cover rounded-lg"
          />
        )}
        
        <p className="text-muted-foreground">{selectedItem?.description}</p>
        
        {/* Modifiers */}
        {modifiers.map((group) => (
          <div key={group.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">{group.name}</h4>
              {group.is_required && (
                <Badge variant="secondary" className="text-xs">Required</Badge>
              )}
            </div>
            <div className="space-y-2">
              {group.options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => onToggleModifier(option.id)}
                  className={`w-full p-3 rounded-lg border text-left transition-colors ${
                    selectedModifiers.includes(option.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`modifier-${option.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{option.name}</span>
                    {option.price > 0 && (
                      <span className="text-muted-foreground">+${option.price.toFixed(2)}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
        
        {/* Special Instructions */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Special Instructions</label>
          <Textarea
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            placeholder="Any special requests?"
            className="resize-none"
            data-testid="special-instructions"
          />
        </div>
        
        {/* Quantity & Add to Cart */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              data-testid="decrease-quantity"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="text-lg font-medium w-8 text-center">{quantity}</span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setQuantity(quantity + 1)}
              data-testid="increase-quantity"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <Button
            onClick={onAddToCart}
            className="btn-pill"
            data-testid="add-to-cart-btn"
          >
            Add ${calculateItemTotal().toFixed(2)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
